import { z } from "zod";

/**
 * Frontend-safe mirror of the enrichment overlay fields the bill screens read.
 *
 * The canonical schema lives in `@medbillai/domain` (`Enrichment.EnrichmentFields`),
 * but that package is server-side: built on `@effect/schema` and `effect`, it
 * pulls in `@medbillai/flux-capacitor`, none of which belong in the app bundle.
 * This re-declares the narrow slice the screens consume as pure Zod, matching
 * the server field shapes exactly (`codeDescriptions`, `priceTransparency`
 * entries, `title`, `overallPriceRating`). The resolver returns this overlay as
 * the `enrichment` JSON on `UserHealthRecord`; `.passthrough()` keeps unread
 * fields (e.g. `practitioner`) from failing the parse.
 */

/** One resolved code, keyed by the bare procedure code in `codeDescriptions`.
 *  `display` is the catalog short description; `taxonomy` is the code system
 *  (CPT / HCPCS / …). Mirrors the server's `CodeDescriptionEntry`. */
export const CodeDescriptionEntry = z.object({
  display: z.string().nullish(),
  longText: z.string().nullish(),
  taxonomy: z.string(),
});
export type CodeDescriptionEntry = z.infer<typeof CodeDescriptionEntry>;

export const PriceTransparencyEntry = z.object({
  median: z.number(),
  p25: z.number(),
  p75: z.number(),
  rating: z.string(),
  allowed: z.number().nullish(),
});
export type PriceTransparencyEntry = z.infer<typeof PriceTransparencyEntry>;

export const EobEnrichment = z
  .object({
    title: z.string().nullish(),
    overallPriceRating: z.string().nullish(),
    codeDescriptions: z.record(z.string(), CodeDescriptionEntry).nullish(),
    priceTransparency: z.record(z.string(), PriceTransparencyEntry).nullish(),
  })
  .passthrough();
export type EobEnrichment = z.infer<typeof EobEnrichment>;
